// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

import RevisedTable from "components/Tables/RevisedTable";

import { useQuery } from '@tanstack/react-query';

import bksAPI from 'services/api/bks';
import groupAPI from 'services/api/groups';
import inspectionAPI from 'modules/inspections/api';
import { processDevicesData } from "utils/device";


import { useNavigate } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";

import PropTypes from 'prop-types';
import { useEffect, useState } from "react";
import { Grid } from "@mui/material";

import { wrapper, loadingScreen } from "utils/wrapper";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import NewAssignmentsCard from "components/Cards/NewAssignmentsCard";
import ReportGenerationCard from "components/Cards/ReportGenerationCard";
import { createDataTableRows } from "utils/device";
import UptimeBarCard from "components/Cards/UptimeBarCard";
import DeviceGroupsPreviewCard from "components/Cards/DeviceGroupsPreviewCard";
import DevicesTableSkeleton from "pages/components/Skeletons/DevicesTableSkeleton";

function DevicesTableView({ map, user }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    // Queries
    bksAPI.loadToken(useAuth0());
    groupAPI.loadToken(useAuth0());
    inspectionAPI.loadToken(useAuth0());

    const navigate = useNavigate();

    const canAssignmentsView = userPermissions.includes('operations');
    const showAdvancedDeviceLevel = userPermissions.includes('devices-advanced:view');
    const canDeviceGroupsView = userPermissions.includes('devices-groups') && selectedOrg != 'clearways';
    const isKerbsides = false; //userPermissions.includes('kerbsides');

    const onRowClicked = function (row, event) {
        // console.log(row);
        // console.log(event);
        goToDevicePage(row.original.uuid);
    }

    const goToDevicePage = (uuid) => {
        let path = '../../devices/' + uuid;
        // console.log(navigate)
        navigate(path);
    }

    const devices = useQuery({
        queryKey: ['getAllDevices', selectedOrg],
        queryFn: async () => await bksAPI.getAllDevices(),
        refetchInterval: 300 * 1000,
        refetchIntervalInBackground: true,
    });

    const all_inspections = useQuery({
        queryKey: ['getAllInspectionsForUser', user, selectedOrg],
        queryFn: async () => {
            return await inspectionAPI.getAllInspectionsForUser(user);
        }
    });

    if (devices.isLoading) {
        return wrapper(<DevicesTableSkeleton {...{ deviceLayout, appearance, isKerbsides, canAssignmentsView, isMobileView: deviceLayout == 'mobile' }} />, controller);
    }

    if (devices.error || all_inspections.error) {
        console.log(devices.error, all_inspections.error)
        return "An error has occurred";
    }

    const devicesData = processDevicesData(devices.dataUpdatedAt, devices.data, showAdvancedDeviceLevel);
    const data_table_data = createDataTableRows(devicesData, showAdvancedDeviceLevel);

    return wrapper((
        <SoftBox borderRadius="20px" color={"white"} width="100%"
            height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"}
            minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
            sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
        >
            <SoftBox mt={1} display="flex" sx={{ height: deviceLayout == 'mobile' ? `calc(100% - 16px)` : `calc(100% - 16px)` }}>
                <SoftBox display="flex" flexDirection="column" borderRadius="10px" color={"white"} width="100%" height={"100%"}>
                    <SoftBox height="100%" sx={() => ({ borderRadius: '16px 16px 0 0' })}>
                        <SoftBox px={1} height="100%" display="flex" flexDirection="column">
                            <SoftBox mb={1}>
                                <Grid container spacing={1}>
                                    <Grid item xxs={12} md={(isKerbsides || canDeviceGroupsView) && (canAssignmentsView && deviceLayout != 'mobile') ? 4 : ((isKerbsides || canDeviceGroupsView) || (canAssignmentsView && deviceLayout != 'mobile') ? 6 : 12)}>
                                        <UptimeBarCard devices={devices} />
                                    </Grid>
                                    {(canAssignmentsView && deviceLayout != 'mobile') && (
                                        <Grid item xxs={12} xl={(isKerbsides || canDeviceGroupsView) ? 4 : 6}>
                                            <NewAssignmentsCard assignment_count={all_inspections?.data?.inspections?.filter((row) => row.status == "unresolved")?.length || null} />
                                        </Grid>
                                    )}
                                    {isKerbsides ? (
                                        <Grid item xxs={12} md={canAssignmentsView && deviceLayout != 'mobile' ? 4 : 6}>
                                            <ReportGenerationCard devices={devicesData} deviceLayout={deviceLayout} />
                                        </Grid>
                                    ) : canDeviceGroupsView && (
                                        <Grid item xxs={12} md={canAssignmentsView && deviceLayout != 'mobile' ? 4 : 6}>
                                            <DeviceGroupsPreviewCard />
                                        </Grid>
                                    )}
                                </Grid>
                            </SoftBox>
                            <SoftBox display="flex" flexDirection="column" height="100%" flex={1} minHeight={0}>
                                <RevisedTable table={data_table_data} showAdvancedDeviceLevel={showAdvancedDeviceLevel} onRowClicked={(row, event) => onRowClicked(row, event)} canSearch />
                            </SoftBox>
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </SoftBox>
    ), controller);
}

DevicesTableView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default DevicesTableView;
